import {memo} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  AppButton,
  AppButtonVariant,
  LetterAvatar,
} from '@/app/shared-components';

import {SettleBalanceModalProps} from './interfaces';

const SettleBalanceModal = memo(
  ({
    open,
    onClose,
    title,
    participantSummary,
    amountLabel,
    avatarName,
    primaryButton,
    secondaryButton,
    memberNameRelated,
  }: SettleBalanceModalProps) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const letterAvatarName = avatarName ?? participantSummary;
    const {
      id: primaryButtonId = 'groups-settle-modal-primary-action',
      label: primaryActionLabel,
      onClick: onPrimaryAction,
      isLoading: isPrimaryLoading = false,
      isDisabled: isPrimaryDisabled = false,
    } = primaryButton ?? {};
    const {
      id: secondaryButtonId = 'groups-settle-modal-secondary-action',
      label: secondaryActionLabel,
      onClick: onSecondaryAction,
      isLoading: isSecondaryLoading = false,
      isDisabled: isSecondaryDisabled = false,
    } = secondaryButton ?? {};

    const content = (
      <Box className="groups-settle-modal">
        <Box className="groups-settle-modal-header">
          <Typography className="groups-settle-modal-title">{title}</Typography>
        </Box>

        <Box className="groups-settle-modal-participant-card">
          <LetterAvatar
            name={letterAvatarName}
            size={40}
            badgeText={memberNameRelated}
          />
          <Box className="groups-settle-modal-participant-content">
            <Typography className="groups-settle-modal-participant-name">
              {participantSummary}
            </Typography>
            <Typography className="groups-settle-modal-participant-amount">
              {amountLabel}
            </Typography>
          </Box>
        </Box>

        <Box className="groups-settle-modal-actions">
          <AppButton
            id={primaryButtonId}
            sentry-label="Groups settle modal primary action"
            label={primaryActionLabel}
            variant={AppButtonVariant.onPrimary}
            onClick={onPrimaryAction}
            isLoading={isPrimaryLoading}
            disabled={isPrimaryDisabled || isPrimaryLoading}
          />
          {secondaryActionLabel && onSecondaryAction && (
            <AppButton
              id={secondaryButtonId}
              label={secondaryActionLabel}
              variant={AppButtonVariant.onQuinary}
              onClick={onSecondaryAction}
              isLoading={isSecondaryLoading}
              disabled={
                isSecondaryDisabled || isSecondaryLoading || isPrimaryLoading
              }
            />
          )}
        </Box>
      </Box>
    );

    if (isDesktop) {
      return (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          PaperProps={{className: 'groups-settle-modal-dialog'}}>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            className="groups-settle-modal-close">
            <CloseIcon />
          </IconButton>
          {content}
        </Dialog>
      );
    }

    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{className: 'groups-settle-modal-drawer'}}>
        <Box className="groups-settle-modal-handle" />
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className="groups-settle-modal-close">
          <CloseIcon />
        </IconButton>
        {content}
      </Drawer>
    );
  },
);

export default SettleBalanceModal;
